import jsPDF from "jspdf";
import "jspdf-autotable";

// --- NOVAS ADIÇÕES: fieldLabels e getFriendlyName FORA do componente ---
// Isso garante que estas funções sejam acessíveis por quem as importar.
const fieldLabels = {
    descricao: "Descrição", area_imovel: "Área do Imóvel (ha)", area_plantio: "Área de Plantio (ha)",
    especie: "Espécie", origem: "Origem", num_arvores_plantadas: "Nº de Árvores Plantadas",
    num_arvores_cortadas: "Nº de Árvores Cortadas", num_arvores_remanescentes: "Nº de Árvores Remanescentes",
    num_arvores_por_hectare: "Nº de Árvores por Hectare", matricula: "Matrícula", data_plantio: "Data de Plantio",
    numero_ccir: "Número CCIR", numero_itr: "Número ITR", proprietario: "Proprietário",
    arrendatario: "Arrendatário", data_contrato: "Data do Contrato", vencimento_contrato: "Vencimento do Contrato",
    municipio: "Município", localidade: "Localidade", altura_desrama: "Altura da Desrama",
    numero_car: "Número do CAR", codigo_cc: "Código CC",
};

const getFriendlyName = (key) => fieldLabels[key] || key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
// --- FIM NOVAS ADIÇÕES ---


/**
 * Formata um valor para exibição, tratando datas e números decimais.
 * @param {*} value - O valor a ser formatado.
 * @param {string} key - A chave do campo, usada para identificar o tipo de formatação.
 * @returns {string|null} - O valor formatado como string, ou null se o valor for inválido.
 */
const formatValue = (value, key) => {
    const dateFields = ["data_plantio", "data_contrato", "vencimento_contrato"];
    if (dateFields.includes(key) && value) {
        const dateValue = new Date(value);
        if (!isNaN(dateValue.getTime())) {
            // Formata para 'DD/MM/YYYY'
            return new Date(dateValue.getTime() + dateValue.getTimezoneOffset() * 60000).toLocaleDateString("pt-BR");
        }
    }
    
    // Tratamento de valores nulos, indefinidos, vazios ou "N/A"
    if (value === null || value === undefined || String(value).trim() === "" || value === "N/A") {
        return "N/A"; 
    }

    const decimalFields = ["area_imovel", "area_plantio", "num_arvores_por_hectare", "altura_desrama"]; // Adicionei altura_desrama se for decimal
    if (decimalFields.includes(key)) {
        const numericValue = Number(String(value).replace(',', '.')); // Garante que vírgulas sejam tratadas como decimais
        if (isFinite(numericValue)) {
            return numericValue.toLocaleString("pt-BR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        }
    }
    
    // Para campos numéricos inteiros
    const integerFields = ["num_arvores_plantadas", "num_arvores_cortadas", "num_arvores_remanescentes"];
    if (integerFields.includes(key)) {
        const numericValue = parseInt(value);
        if (isFinite(numericValue)) {
            return numericValue.toLocaleString("pt-BR");
        }
    }

    return String(value);
};

/**
 * Gera um relatório em PDF com os detalhes de um imóvel.
 * @param {object} params - Os parâmetros para a geração do relatório.
 * @param {object} params.imovel - O objeto com os dados do imóvel.
 * @param {object} params.selectedFields - Um objeto indicando quais campos foram selecionados para o relatório.
 * @param {string} params.logo - A imagem do logo para o cabeçalho do PDF.
 */
export const generateImovelReport = ({ imovel, selectedFields, logo }) => { // getFriendlyName não precisa mais ser passado, pois já está no escopo
    if (!imovel) {
        alert("Dados do imóvel não encontrados para gerar o relatório.");
        return;
    }

    const doc = new jsPDF();
    // Definir cores da sua paleta em RGB
    const primaryColor = [40, 167, 69];     // --c-primary
    const primaryDarkColor = [33, 136, 56]; // --c-primary-dark
    const textColor = [33, 37, 41];         // --c-text-primary
    const secondaryTextColor = [108, 117, 125]; // --c-text-secondary
    const lightBgColor = [248, 251, 248];   // --c-bg (um verde muito claro)
    const borderColor = [222, 226, 230];    // --c-border

    // Adiciona uma fonte Poppins (exemplo, você pode precisar embedar se não estiver disponível)
    // Se 'Poppins' não estiver disponível por padrão, jspdf usará a fonte padrão (Helvetica).
    // Para fontes customizadas, você precisaria de:
    // doc.addFont('caminho/para/Poppins-Regular.ttf', 'Poppins', 'normal');
    // doc.addFont('caminho/para/Poppins-Bold.ttf', 'Poppins', 'bold');
    // doc.setFont('Poppins'); 

    // Função para adicionar cabeçalho e rodapé em cada página
    const addHeaderAndFooter = (pageNumber) => {
        // Logo
        if (logo) {
            const logoWidth = 35; // Tamanho um pouco menor para o logo
            const logoHeight = 14;
            const logoX = 15; // Mais afastado da borda
            doc.addImage(logo, "PNG", logoX, 12, logoWidth, logoHeight);
        }

        // Título fixo no cabeçalho
        doc.setFontSize(10);
        doc.setTextColor(secondaryTextColor[0], secondaryTextColor[1], secondaryTextColor[2]);
        doc.text("Relatório Detalhado de Imóvel", doc.internal.pageSize.width - 15, 18, { align: "right" });

        // Linha separadora do cabeçalho
        doc.setDrawColor(borderColor[0], borderColor[1], borderColor[2]);
        doc.setLineWidth(0.3); // Linha mais fina
        doc.line(15, 28, doc.internal.pageSize.width - 15, 28); // Ajusta a linha para as margens

        // Rodapé
        doc.setFontSize(8);
        doc.setTextColor(secondaryTextColor[0], secondaryTextColor[1], secondaryTextColor[2]);
        doc.setFont("helvetica", "normal"); // Reset para fonte normal para o rodapé
        doc.text(`Gerado por Sis Florestal - ${new Date().toLocaleDateString("pt-BR")}`, 15, doc.internal.pageSize.height - 15);
        doc.text(`Página ${pageNumber}`, doc.internal.pageSize.width - 15, doc.internal.pageSize.height - 15, { align: "right" });
    };

    // Adiciona cabeçalho e rodapé para a primeira página
    addHeaderAndFooter(1);

    let yOffset = 40; // Posição inicial do conteúdo após o cabeçalho

    // Título principal do Imóvel
    doc.setFont("helvetica", "bold");
    doc.setFontSize(26); // Título maior
    doc.setTextColor(primaryDarkColor[0], primaryDarkColor[1], primaryDarkColor[2]);
    doc.text(imovel.descricao, 15, yOffset); // Margem esquerda de 15

    yOffset += 12;
    doc.setFontSize(14); // Subtítulo para Código CC e Proprietário
    doc.setTextColor(textColor[0], textColor[1], textColor[2]);
    doc.setFont("helvetica", "normal");
    doc.text(`Código CC: ${imovel.codigo_cc || 'N/A'}`, 15, yOffset);
    yOffset += 8;
    doc.text(`Proprietário: ${imovel.proprietario || 'N/A'}`, 15, yOffset);
    yOffset += 20; // Espaço antes da tabela


    // Prepara os dados para jspdf-autotable
    const head = [['Atributo', 'Valor']];
    const body = Object.keys(selectedFields)
        .filter((key) => selectedFields[key] && formatValue(imovel[key], key) !== "N/A") // Filtra e exclui "N/A"
        .map((key) => {
            const label = getFriendlyName(key);
            const value = formatValue(imovel[key], key);
            return [label, value];
        });

    if (body.length === 0) {
        alert("Nenhum campo selecionado ou com dados válidos para gerar o relatório.");
        return;
    }

    // Configurações da tabela com jspdf-autotable
    doc.autoTable({
        startY: yOffset,
        head: head,
        body: body,
        theme: 'striped', // Estilo zebrado
        styles: {
            fontSize: 10,
            cellPadding: 3,
            valign: 'middle',
            font: 'helvetica', // Pode ser "times" ou "courier" por padrão. Para "Poppins", precisa embedar.
            textColor: textColor,
            lineColor: borderColor,
            lineWidth: 0.1,
            cellWidth: 'wrap', // Permite que o texto quebre para caber na célula
        },
        headStyles: {
            fillColor: primaryColor, // Cor de fundo do cabeçalho
            textColor: [255, 255, 255], // Cor do texto do cabeçalho (branco)
            fontStyle: 'bold',
            fontSize: 10,
            halign: 'left',
            cellPadding: 4,
            minCellHeight: 12, // Altura mínima para o cabeçalho
        },
        bodyStyles: {
            fillColor: [255, 255, 255], // Fundo padrão do corpo
            minCellHeight: 10, // Altura mínima para as células do corpo
        },
        alternateRowStyles: {
            fillColor: lightBgColor, // Cor para linhas alternadas
        },
        columnStyles: {
            0: { fontStyle: 'bold', minCellWidth: 60, cellWidth: 'auto' }, // Coluna do atributo em negrito, largura automática
            1: { minCellWidth: 80, cellWidth: 'auto' }, // Coluna do valor, largura automática
        },
        didDrawPage: (data) => {
            // Adiciona cabeçalho e rodapé em cada nova página
            // data.pageNumber é a página atual que está sendo desenhada (1, 2, 3...)
            addHeaderAndFooter(data.pageNumber);
        },
        margin: { top: 35, right: 15, bottom: 20, left: 15 }, // Ajusta as margens da tabela no documento
        rowPageBreak: 'auto', // Quebra de linha automática nas linhas
        tableWidth: 'auto' // A tabela ocupa a largura disponível automaticamente
    });

    doc.save(`relatorio_${imovel.descricao.replace(/\s+/g, '_')}.pdf`);
};